import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Stack, TertiaryButton, Text } from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'
import { useErrorToast } from '@enechain/eposition-ui'

import { EmptyOrderPatternSection } from '~/pages/Simulation/components/OrderPatternInputArea/EmptyOrderPatternSection'
import { SimulationOrderPatternForm } from '~/pages/Simulation/components/OrderPatternInputArea/SimulationOrderPatternForm'
import { useInitialOrderPatternForm } from '~/pages/Simulation/hooks/useInitialOrderPatternForm'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

const MAX_ORDER_PATTERNS = 10

export const SimulationInputArea: React.FC = () => {
  const formMethods = useFormContext<OrderSimulationSchema>()
  const { fields: orderPatternFields, append: appendOrderPattern } =
    useFieldArray({
      control: formMethods.control,
      name: 'orderPattern',
    })
  const { t } = useTranslation(['page', 'common'])
  const initialOrderPatternForm = useInitialOrderPatternForm()
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'SimulationInputArea/error',
  })

  const handleAddOrderPattern = (): void => {
    if (orderPatternFields.length >= MAX_ORDER_PATTERNS) {
      showErrorToast({
        toastMessage: t(
          'page:simulation.error_message.exceed_max_order_patterns',
          {
            value: MAX_ORDER_PATTERNS,
          },
        ),
      })
      return
    }
    appendOrderPattern(initialOrderPatternForm)
  }

  return (
    <Stack borderColor="Border.midEmphasis" gap="4">
      <Text textStyle="headerMd">{t('page:simulation.input_order')}</Text>
      {orderPatternFields.map((field, index) => (
        <SimulationOrderPatternForm key={field.id} index={index} />
      ))}
      {orderPatternFields.length === 0 ? (
        <EmptyOrderPatternSection
          handleAddOrderPattern={handleAddOrderPattern}
        />
      ) : (
        <Box
          width="full"
          display="flex"
          justifyContent="center"
          padding="2"
          backgroundColor="Surface.secondary"
          borderRadius="radiiLg"
        >
          <TertiaryButton
            leftIcon={<FaPlusRegular />}
            onClick={handleAddOrderPattern}
          >
            {t('common:action.neutral.add', {
              value: t('page:simulation.input_order'),
            })}
          </TertiaryButton>
        </Box>
      )}
    </Stack>
  )
}
